import { Divider, Checkbox, Typography, Grid, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import React from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import { humanReadableString } from 'utils/stringUtils';

type LanguageMap = {
  languagesPerGroupedAgreement: { serviceName: string; value: string }[];
  languagesPerAgreement: { agreementId: string; value: string }[];
  languagesPerDocument: { documentUid: string; value: string }[];
};

type DocumentDetails = {
  documentUid: string;
  documentStatus?: string;
  documentLanguages?: string[];
  documentFormCode?: string;
};

type ProductDocumentsToDistributeDialogDocumentsSectionProps = {
  documents: DocumentDetails[];
  checkedDocuments: string[];
  handleSelectDocument: (doc: DocumentDetails) => void;
  setLanguangeMap: React.Dispatch<React.SetStateAction<LanguageMap>>;
};

const ProductDocumentsToDistributeDialogDocumentsSection = ({
  documents,
  checkedDocuments,
  handleSelectDocument,
  setLanguangeMap,
}: ProductDocumentsToDistributeDialogDocumentsSectionProps) => {
  const [selectedLanguageForAgreementDocuments, setSelectedLanguageForAgreementDocuments] = React.useState<
    { key: string; value: string }[]
  >([]);

  const handleChange = (e: { target: { value: string } } | null, key: string) => {
    if (!e) {
      setSelectedLanguageForAgreementDocuments((prev) => prev.filter((a) => a.key !== key));
      setLanguangeMap((prev) => ({
        ...prev,
        languagesPerDocument: prev?.languagesPerDocument.filter((a) => a.documentUid !== key),
      }));
      return;
    }
    setSelectedLanguageForAgreementDocuments((prev) => [
      ...(prev?.filter((d) => d.key !== key) ?? []),
      { key: key, value: e.target.value },
    ]);
    setLanguangeMap((prev) => ({
      ...prev,
      languagesPerDocument: [
        ...(prev?.languagesPerDocument?.filter((d) => d.documentUid !== key) ?? []),
        { documentUid: key, value: e.target.value },
      ],
    }));
  };

  return documents.map((doc: DocumentDetails, docIndex: number) => (
    <Grid container key={docIndex}>
      <Grid
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mt: 1,
          ml: 2,
        }}
        container
      >
        {docIndex !== 0 && <Divider sx={{ height: '2px', width: '100%', mt: 0.3, mb: 0.3 }} />}
        <Grid
          item
          xs={1}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Checkbox
            data-testid={`document-checkbox`}
            checked={checkedDocuments.includes(doc.documentUid)}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleSelectDocument(doc);
            }}
          />
        </Grid>
        <Grid item xs={7}>
          <Typography variant="body2">Document Status: {humanReadableString(doc.documentStatus)}</Typography>
          <Typography variant="body2">Languages: {doc?.documentLanguages?.join(', ')}</Typography>
          <Typography variant="body2">Form Code: {doc.documentFormCode}</Typography>
        </Grid>
        <Grid item xs={4}>
          <FormControl variant="outlined" sx={{ width: ' 11em', borderRadius: 0 }} size="small">
            <InputLabel sx={{ mb: 1 }} id="demo-simple-select-filled-label">
              Languange
            </InputLabel>
            <Select
              endAdornment={
                selectedLanguageForAgreementDocuments?.filter((d) => d.key === doc.documentUid)[0] && (
                  <ClearIcon
                    sx={{ height: '0.5em', mr: 1, cursor: 'pointer' }}
                    onClick={() => handleChange(null, doc.documentUid)}
                  />
                )
              }
              label="Languange"
              onChange={(e) => handleChange(e, doc.documentUid)}
              value={selectedLanguageForAgreementDocuments?.filter((d) => d.key === doc.documentUid)[0]?.value ?? ''}
            >
              {doc.documentLanguages?.map((doc: string) => (
                <MenuItem key={doc} value={doc}>
                  {doc}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Grid>
  ));
};

export default React.memo(ProductDocumentsToDistributeDialogDocumentsSection);
